import type { SubstrateManifest } from '../manifest';
import type { AssetRecord, PublishedRecord, RecipeRecord, RunRecord } from '../runtime';

type BenchSection = 'composer' | 'assets' | 'recipes' | 'runs' | 'published';

const SECTION_COPY: Record<Exclude<BenchSection, 'composer'>, { title: string; description: string }> = {
  assets: {
    title: 'Assets',
    description: 'Typed artifacts produced or consumed by substrate runs. Phase 4 wires this to the local V1 repository.',
  },
  recipes: {
    title: 'Recipes',
    description: 'Saved substrate graphs and starter compositions. Phase 2 turns graph state into persisted recipe templates.',
  },
  runs: {
    title: 'Runs',
    description: 'Execution history, wave status, decisions, and asset deltas. Phase 3 introduces durable run records.',
  },
  published: {
    title: 'Published',
    description: 'Receipts from sink primitives and publishable artifacts. Phase 4 fills this with local V1 data.',
  },
};

export function WorkbenchSubroutePanel({
  section,
  manifest,
  assets,
  recipes,
  runs,
  published,
}: {
  section: Exclude<BenchSection, 'composer'>;
  manifest: SubstrateManifest;
  assets: AssetRecord[];
  recipes: RecipeRecord[];
  runs: RunRecord[];
  published: PublishedRecord[];
}) {
  const copy = SECTION_COPY[section];
  const countBySection = {
    assets: assets.length,
    recipes: recipes.length,
    runs: runs.length,
    published: published.length,
  }[section];

  return (
    <section className="sp-widget" style={{ minHeight: 420 }}>
      <div className="sp-widget-header">
        <span>{copy.title}</span>
        <span className="mono">{countBySection} local records</span>
      </div>
      <div className="sp-widget-body" style={{ display: 'grid', gap: 16 }}>
        <div className="sp-empty" style={{ textAlign: 'left' }}>
          <h2 style={{ color: 'var(--sp-text)', marginTop: 0 }}>{copy.title}</h2>
          <p style={{ marginBottom: 0 }}>{copy.description}</p>
        </div>
        {section === 'assets' ? <AssetList assets={assets} manifest={manifest} /> : null}
        {section === 'recipes' ? <RecipeList recipes={recipes} manifest={manifest} /> : null}
        {section === 'runs' ? <RunList runs={runs} /> : null}
        {section === 'published' ? <PublishedList published={published} /> : null}
      </div>
    </section>
  );
}

export type { BenchSection };

function EmptyState({ label }: { label: string }) {
  return (
    <div style={{ color: 'var(--sp-text-muted)', fontSize: 13 }}>
      No {label} yet. Run the default substrate composition from the Workbench tab to create local records.
    </div>
  );
}

function AssetList({ assets, manifest }: { assets: AssetRecord[]; manifest: SubstrateManifest }) {
  if (assets.length === 0) return <EmptyState label="assets" />;
  const labels = new Map(manifest.asset_types.map((assetType) => [assetType.id, assetType.label]));
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {assets.map((asset) => (
        <article key={asset.id} style={{ background: 'var(--sp-bg)', border: '1px solid var(--sp-border)', borderRadius: 'var(--sp-radius)', padding: 10 }}>
          <strong style={{ color: 'var(--sp-text)' }}>{asset.label || labels.get(asset.type) || asset.type}</strong>
          <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>{asset.type} · {asset.primitiveId}</div>
          <pre style={{ color: 'var(--sp-text-muted)', fontSize: 11, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(asset.payload, null, 2)}
          </pre>
        </article>
      ))}
    </div>
  );
}

function RecipeList({ recipes, manifest }: { recipes: RecipeRecord[]; manifest: SubstrateManifest }) {
  const allRecipes = recipes.length > 0 ? recipes : manifest.default_recipes.map((recipe) => ({
    id: recipe.id,
    label: recipe.label,
    graph: { id: recipe.id, label: recipe.label, nodes: [], edges: [], updatedAt: '' },
    createdAt: '',
    updatedAt: '',
  }));
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {allRecipes.map((recipe) => (
        <article key={recipe.id} style={{ background: 'var(--sp-bg)', border: '1px solid var(--sp-border)', borderRadius: 'var(--sp-radius)', padding: 10 }}>
          <strong style={{ color: 'var(--sp-text)' }}>{recipe.label}</strong>
          <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
            {recipe.graph.nodes.length || manifest.default_recipes[0]?.primitive_ids.length || 0} primitives
          </div>
        </article>
      ))}
    </div>
  );
}

function RunList({ runs }: { runs: RunRecord[] }) {
  if (runs.length === 0) return <EmptyState label="runs" />;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {runs.map((run) => (
        <article key={run.id} style={{ background: 'var(--sp-bg)', border: '1px solid var(--sp-border)', borderRadius: 'var(--sp-radius)', padding: 10 }}>
          <strong style={{ color: 'var(--sp-text)' }}>{run.graphLabel}</strong>
          <div className="mono" style={{ color: run.status === 'completed' ? 'var(--sp-ok)' : run.status === 'paused' ? 'var(--sp-warn)' : 'var(--sp-text-muted)', fontSize: 11 }}>
            {run.status} · {run.nodes.length} nodes · {run.decisions.length} decisions · {run.assetDeltas.length} assets
          </div>
        </article>
      ))}
    </div>
  );
}

function PublishedList({ published }: { published: PublishedRecord[] }) {
  if (published.length === 0) return <EmptyState label="published receipts" />;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {published.map((record) => (
        <article key={record.id} style={{ background: 'var(--sp-bg)', border: '1px solid var(--sp-border)', borderRadius: 'var(--sp-radius)', padding: 10 }}>
          <strong style={{ color: 'var(--sp-text)' }}>{record.sinkType} → {record.destination}</strong>
          <div className="mono" style={{ color: 'var(--sp-ok)', fontSize: 11 }}>{record.status} · {record.runId}</div>
        </article>
      ))}
    </div>
  );
}
